import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export type ToastType = 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastApi {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_MS = 4000;

/**
 * Proveedor de toasts (feedback inmediato de acciones, UX-FB-01/UX-COPY-01). La
 * región es `aria-live="polite"` para que el lector de pantalla anuncie el
 * resultado. Auto-descarta a los 4s. Se monta una vez alto en el árbol.
 */
export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((prev) => [...prev, { id, message, type }]);
    const handle = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, DISMISS_MS);
    timers.current.push(handle);
  }, []);

  // Limpia timers pendientes al desmontar (evita setState sobre árbol desmontado).
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="toast-region" role="region" aria-live="polite" aria-label="Notificaciones">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`} role="status">
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Acceso al toast. Fuera de `ToastProvider` lanza (fail-fast en desarrollo). */
// eslint-disable-next-line react-refresh/only-export-components -- hook y provider conviven por cohesión
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return ctx;
}
